import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { InputText } from "primereact/inputtext";
import { confirmDialog } from "primereact/confirmdialog";
import PrimaryButton from "../../shared/Button/PrimaryButton";
import { getPermissionCatalog, getSubAdmins, updateSubAdminStatus } from "../../redux/actions/subAdminAction";
import { hasAnyPermission, hasPermission } from "../../utils/adminPermissions";
import { exportTableToCsv } from "../../utils/exportTableCsv";

export default function SubAdmins() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { allSubAdmins } = useSelector((state) => state.subAdmins);
    const { userProfile } = useSelector((state) => state.profile);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");

    useEffect(() => {
        dispatch(getSubAdmins(setLoading));
        dispatch(getPermissionCatalog());
    }, [dispatch]);

    const filteredAdmins = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return allSubAdmins;
        return allSubAdmins.filter((admin) =>
            [admin.name, admin.email, admin.role, ...(admin.permissions || [])]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(query))
        );
    }, [allSubAdmins, search]);

    const canEdit = hasAnyPermission(userProfile, ["sub_admin.update", "sub_admin.manage_permissions"]);
    const canCreate = hasPermission(userProfile, "sub_admin.create");
    const canDeactivate = hasPermission(userProfile, "sub_admin.deactivate");
    const canReset = hasPermission(userProfile, "sub_admin.reset_password");

    const permissionsBody = (row) => (
        <span title={(row.permissions || []).join(", ")}>
            {(row.permissions || []).length ? `${row.permissions.length} permission(s)` : "No permissions"}
        </span>
    );

    const statusBody = (row) => (
        <Tag value={row.isActive === false ? "Inactive" : "Active"} severity={row.isActive === false ? "danger" : "success"} />
    );

    const actionsBody = (row) => (
        <div className="flex gap-2">
            {canEdit && <i className="pi pi-pencil cursor-pointer" title="Edit" onClick={() => navigate(`/sub-admin/edit/${row._id}`)} />}
            {canDeactivate && (
                <i
                    className={`pi ${row.isActive === false ? "pi-check-circle" : "pi-ban"} cursor-pointer`}
                    title={row.isActive === false ? "Activate" : "Deactivate"}
                    onClick={() => {
                        const activate = row.isActive === false;
                        confirmDialog({
                            message: `Are you sure you want to ${activate ? "activate" : "deactivate"} ${row.name || row.email}?`,
                            header: `${activate ? "Activate" : "Deactivate"} Sub-admin`,
                            icon: "pi pi-exclamation-triangle",
                            acceptLabel: activate ? "Activate" : "Deactivate",
                            rejectLabel: "Cancel",
                            acceptClassName: activate ? "p-button-success" : "p-button-danger",
                            accept: () => dispatch(updateSubAdminStatus(row._id, activate)),
                        });
                    }}
                />
            )}
            {canReset && (
                <i
                    className="pi pi-key cursor-pointer"
                    title="Reset password"
                    onClick={() => navigate(`/sub-admin/reset-password/${row._id}`)}
                />
            )}
        </div>
    );

    const exportAll = () => {
        exportTableToCsv("sub-admins.csv", filteredAdmins, [
            { header: "Name", value: "name" },
            { header: "Email", value: "email" },
            { header: "Status", value: (row) => (row.isActive === false ? "Inactive" : "Active") },
            { header: "Permissions", value: (row) => (row.permissions || []).join(", ") },
            { header: "Created", value: (row) => (row.time ? new Date(row.time).toLocaleDateString() : "-") },
        ]);
    };

    return (
        <div className="col-12">
            <div className="card">
                <div className="flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                    <h5 className="m-0">Sub-admin Management</h5>
                    <div className="flex align-items-center gap-2">
                        <span className="p-input-icon-left">
                            <i className="pi pi-search" />
                            <InputText value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search" />
                        </span>
                        {canCreate && <PrimaryButton label="Add Sub-admin" onClick={() => navigate("/sub-admin/add")} />}
                        <PrimaryButton
                            label="Export CSV"
                            icon="pi pi-download"
                            onClick={exportAll}
                            disabled={!filteredAdmins.length}
                            severity="success"
                        />
                    </div>
                </div>
                <DataTable value={filteredAdmins} loading={loading} paginator rows={10} rowsPerPageOptions={[10, 20, 50]} emptyMessage="No sub-admins found." showGridlines>
                    <Column field="name" header="Name" />
                    <Column field="email" header="Email" />
                    <Column body={statusBody} header="Status" />
                    <Column body={permissionsBody} header="Permissions" />
                    <Column field="time" header="Created" body={(row) => row.time ? new Date(row.time).toLocaleDateString() : "-"} />
                    <Column body={actionsBody} header="Actions" />
                </DataTable>
            </div>
        </div>
    );
}
